import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Text,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  Menu,
  MenuButton,
  MenuList,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { HistoryProjectBlock } from "../../components/ProjectBlocks/SingleProjectBlock/HistoryProjectBlock";
import { Layout } from "../../components/Layout/Layout";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import { fetchDeleteProject, fetchSingleProject } from "../../store/slices/projects";
import PageLoaderWrapper from "../../components/PageLoaderWrapper";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FiEdit, FiTrash2, FiClock, FiFolder } from "react-icons/fi";
import { formatDate } from "../../helpers/formatDate";
import { ViewInformationBlock } from "@/components/ViewInformationBlock/ViewInformationBlock";

export const SingleProjectPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { currentProject, isLoading } = useAppSelector((state) => state.projects);
  const [isShowHistory, setShowHistory] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchSingleProject({ id: Number(id) }));
  }, [id, dispatch]);
  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных проекта...">
        <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
          <Text color="gray.500" cursor="pointer" onClick={() => navigate("/projects")} display="flex" alignItems="center" gap="8px">
            <ArrowBackIcon />К списку проектов
          </Text>
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize={30} fontWeight={700} color="#3b515e">
              Просмотр проекта
            </Text>
            <Flex gap="16px">
              {currentProject?.can_edit && (
                <Button leftIcon={<Icon as={FiEdit as any} boxSize={4} />} onClick={() => navigate("edit")}>
                  Редактировать
                </Button>
              )}
              <Menu>
                <MenuButton leftIcon={<Icon as={FiClock as any} boxSize={4} />} as={Button} variant="outline">
                  История
                </MenuButton>
                <MenuList maxH="400px" overflowY="auto" w="400px">
                  {currentProject?.activities?.length ? (
                    currentProject.activities.map((activity, idx) => (
                      <Box key={idx} p="12px" borderBottom="1px solid #e2e8f0">
                        <Text fontWeight={600} fontSize="sm" marginBottom="4px">
                          {formatDate(activity.created_at, true)}
                        </Text>
                        <Text fontSize="sm" color="gray.600" whiteSpace="pre-line">
                          {activity.action}
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Box p="12px">
                      <Text fontSize="sm" color="gray.500">
                        История изменений пуста
                      </Text>
                    </Box>
                  )}
                </MenuList>
              </Menu>
              {currentProject?.can_remove && (
                <Button leftIcon={<Icon as={FiTrash2 as any} boxSize={4} />} colorScheme="red" onClick={() => setDeleteOpen(true)}>
                  Удалить
                </Button>
              )}
            </Flex>
          </Flex>
          {currentProject?.can_remove && (
            <Modal isCentered isOpen={isDeleteOpen} onClose={() => setDeleteOpen(false)}>
              <ModalOverlay backdropFilter="blur(3px)" />
              <ModalContent maxW="50vw" gap="24px" px="24px" paddingTop="32px" paddingBottom="24px">
                <ModalHeader p={0}>Подтвердите удаление</ModalHeader>
                <ModalBody>
                  <Text>
                    Вы действительно хотите удалить проект <b>{currentProject?.name}</b>?
                  </Text>
                </ModalBody>
                <ModalFooter p={0} pt="8px">
                  <Flex gap="12px">
                    <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                      Отмена
                    </Button>
                    <Button
                      colorScheme="red"
                      isLoading={isDeleting}
                      onClick={() => {
                        if (!id) return;
                        setIsDeleting(true);
                        dispatch(fetchDeleteProject(Number(id))).then((res: any) => {
                          setIsDeleting(false);
                          if (res.payload === 1) {
                            setDeleteOpen(false);
                            navigate("/projects");
                          }
                        });
                      }}
                    >
                      Удалить
                    </Button>
                  </Flex>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}
          <Flex flexDirection="column" gap="32px">
            <Box>
              <Flex flexDirection="column" gap="16px" flex={1} minW={0}>
                <ViewInformationBlock icon={<Icon as={FiFolder as any} boxSize={5} color="#3b515e" />} title="Описание проекта">
                  <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
                    {currentProject?.description || "—"}
                  </Text>
                </ViewInformationBlock>

                <ViewInformationBlock icon={<></>} title="Детали проекта">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Field label="Название проекта" value={currentProject?.name || "—"} isBold />
                    <Field label="Заказчик" value={currentProject?.customer?.name || "—"} />
                    <Field label="Юр. лицо" value={currentProject?.legal_entity?.name || "—"} />
                    <Field label="Статус проекта" value={currentProject?.status?.name || "—"} />
                    <Field label="Формат работы" value={(currentProject as any)?.project_format?.name || "—"} />
                    <Field label="Ответственный аккаунт-менеджер" value={currentProject?.responsible_user?.name || "—"} />
                    <Field label="Контактные данные" value={(currentProject as any)?.contact || "—"} />
                  </SimpleGrid>

                  <Box borderTop="1px solid #E5E7EB" mt="16px" pt="16px">
                    <Text fontSize="14px" color="#6B7280" fontWeight={700} mb="12px">
                      Ссылки
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                      <Field label="Ссылка на проект (Bitrix 24)" value={(currentProject as any)?.bitrix_project_link || "—"} isLink />
                      <Field label="Ссылка на договор (Bitrix 24)" value={(currentProject as any)?.bitrix_contract_link || "—"} isLink />
                    </SimpleGrid>
                  </Box>

                  <Box borderTop="1px solid #E5E7EB" mt="16px" pt="16px">
                    <Text fontSize="14px" color="#6B7280" fontWeight={700} mb="12px">
                      Временные рамки
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                      <Field label="Дата старта" value={currentProject?.start_at ? formatDate(currentProject.start_at) : "—"} />
                      <Field
                        label="Дата планового завершения"
                        value={currentProject?.planned_end_at ? formatDate(currentProject.planned_end_at) : "—"}
                      />
                    </SimpleGrid>
                  </Box>
                </ViewInformationBlock>

                <ViewInformationBlock icon={<></>} title="Комментарий">
                  <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
                    {currentProject?.comments || "—"}
                  </Text>
                </ViewInformationBlock>
              </Flex>

              <Modal isCentered isOpen={isShowHistory} onClose={() => setShowHistory(false)} size="6xl" scrollBehavior="inside">
                <ModalOverlay backdropFilter="blur(3px)" />
                <ModalContent maxW="60vw" px="24px" paddingTop="32px" paddingBottom="24px">
                  <ModalHeader p={0}>История изменений</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody p={0} pt="8px" maxH="70vh" overflowY="auto">
                    <HistoryProjectBlock />
                  </ModalBody>
                </ModalContent>
              </Modal>
            </Box>
          </Flex>
        </Flex>
      </PageLoaderWrapper>
    </Layout>
  );
};

function Field({ label, value, isLink, isBold = false }: { label: string; value: string; isLink?: boolean; isBold?: boolean }) {
  return (
    <Flex flexDirection="column" gap="6px" minW={0}>
      <Text color="#65758b" fontSize={14} lineHeight="20px" fontWeight={500}>
        {label}
      </Text>
      {isLink && value && value !== "—" ? (
        <a href={value} target="_blank" rel="noreferrer" style={{ color: "#2563EB", textDecoration: "none", wordBreak: "break-word", fontSize: 16 }}>
          {value}
        </a>
      ) : (
        <Text color="#3b515e" fontSize={16} lineHeight="24px" fontWeight={isBold ? 600 : 400}>
          {value}
        </Text>
      )}
    </Flex>
  );
}
